import { type PropsWithChildren, useMemo } from 'react';

import { units } from '@dynatrace-sdk/units';

import { ChartVariantContext } from '../../core/context/ChartVariantContext.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import type { ChartDatapoint } from '../../core/types/chart-data.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { FlameTooltip } from '../shared/components/tooltip/FlameTooltip.js';
import {
  DEPTH_FIELD,
  DISPLAY_NAME_FLAME_CHART,
  END_FIELD,
  FLAME_ORIGINAL_IDX,
  START_FIELD,
  VALUE_FIELD,
  XY_DEFAULTS,
} from '../shared/constants.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { FlameChartTooltipSlot as Tooltip } from '../shared/slots/Tooltip.js';
import type { FlameChartProps } from '../shared/types/flame-chart.js';
import { orderSpansHierarchically } from '../shared/utils/order-spans-hierarchically.js';

const CROSSHAIR_VARIANT = {
  crosshair: { horizontalHidden: true, cursor: 'default' as const },
};

/**
 * @internal
 */
export const FlameChartRoot = /* @__PURE__ */ <
  TData extends ChartDatapoint = ChartDatapoint,
>({
  data,
  idAccessor,
  parentIdAccessor,
  startAccessor,
  endAccessor,
  selfValueAccessor,
  labelAccessor,
  colorPalette,
  orientation = 'bottom-up',
  truncationMode,
  valueFormatter,
  minTime,
  currentZoom,
  seriesActions,
  height,
  children,
}: PropsWithChildren<FlameChartProps<TData>>) => {
  const orderedData = useMemo(() => {
    const withIndex = data.map((datum, index) => ({
      ...datum,
      [FLAME_ORIGINAL_IDX]: index,
    }));
    const ordered = orderSpansHierarchically(withIndex, {
      spanIdAccessor: idAccessor,
      parentIdAccessor,
      x0Accessor: startAccessor,
      x1Accessor: endAccessor,
    });

    const offset = minTime ?? 0;
    const output: ChartDatapoint[] = new Array(ordered.length);
    for (let i = 0; i < ordered.length; i++) {
      const span = ordered[i];
      const start = Number(span[startAccessor] ?? 0);
      const end = Number(span[endAccessor] ?? 0);
      output[i] = {
        ...span,
        [START_FIELD]: start - offset,
        [END_FIELD]: end - offset,
        [VALUE_FIELD]: end - start,
      };
    }
    return output;
  }, [data, idAccessor, parentIdAccessor, startAccessor, endAccessor, minTime]);

  const normalizedZoom = useMemo<[number, number] | undefined>(() => {
    if (!currentZoom) {
      return undefined;
    }
    const offset = minTime ?? 0;
    return [currentZoom[0] - offset, currentZoom[1] - offset];
  }, [currentZoom, minTime]);

  const { customTooltipTemplate, colorRules } = iterateSlots<TData>(children);

  return (
    <ChartVariantContext.Provider value={CROSSHAIR_VARIANT}>
      <XYChart
        data={orderedData}
        truncationMode={truncationMode}
        height={height}
      >
        <XYChart.RectSeries
          x0Accessor={START_FIELD}
          x1Accessor={END_FIELD}
          y0Accessor={DEPTH_FIELD}
          valueAccessor={VALUE_FIELD}
          colorPalette={colorPalette}
          _labelAccessor={labelAccessor}
          xAxisId={XY_DEFAULTS.xAxisId}
          yAxisId={XY_DEFAULTS.yAxisId}
        />
        <XYChart.XAxis
          id={XY_DEFAULTS.xAxisId}
          type="numerical"
          position={orientation === 'bottom-up' ? 'bottom' : 'top'}
          formatter={{ input: units.time.millisecond }}
          currentZoom={normalizedZoom}
          infiniteZoom
        />
        <XYChart.YAxis
          id={XY_DEFAULTS.yAxisId}
          type="categorical"
          position="left"
          hidden
          reversed={orientation === 'bottom-up'}
        />
        <XYChart.Legend hidden />
        <XYChart.Toolbar hidden />
        {colorRules}
        <XYChart.Tooltip>
          {(payload) => {
            const datapoint = payload.rectPayload?.datapoint as
              | TData
              | undefined;

            if (!datapoint) {
              return;
            }

            if (customTooltipTemplate) {
              const template = customTooltipTemplate(datapoint);

              if (template === null) {
                return null;
              }
              if (template) {
                return template;
              }
            }

            return (
              <FlameTooltip
                payload={payload}
                data={data}
                labelAccessor={labelAccessor}
                valueField={VALUE_FIELD}
                selfValueAccessor={selfValueAccessor}
                valueFormatter={valueFormatter}
                seriesActions={seriesActions}
              />
            );
          }}
        </XYChart.Tooltip>
      </XYChart>
    </ChartVariantContext.Provider>
  );
};

/** @internal */
export const FlameChart = Object.assign(FlameChartRoot, {
  Tooltip,
  ColorRule,
});

(FlameChart as typeof FlameChart & { displayName: string }).displayName =
  DISPLAY_NAME_FLAME_CHART;
